import { IconAccessTime, IconIsolationMode } from 'assets/img';
import type { Column } from 'react-table';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { BoxAction, BoxIcon } from 'shared/components/planner/common/utils';
import { TFunctionNonStrict } from 'i18next';
import {
  PartnerPlacementsCellProp,
  PartnerPlacementsColumnDefinition,
} from '../partner-placements.types';

const getPartnerPlacementsColumnDefinition: (
  t: TFunctionNonStrict<'partner-screen-specific', undefined>
) => Column<PartnerPlacementsColumnDefinition>[] = (t) => {
  const workerName = {
    Header: () => (
      <Header>{t('placements.table_headers.worker', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'workerName',
    Cell: WorkerCell,
    width: '200px',
  } as Column<PartnerPlacementsColumnDefinition>;

  const companyBranchOfficeInfo = {
    Header: () => (
      <Header>
        {t('placements.table_headers.company_and_branch_office', { ns: 'partner-screen-specific' })}
      </Header>
    ),
    accessor: 'companyBranchOfficeInfo',
    Cell: CompanyBranchOfficeInfoCell,
    width: '200px',
  } as Column<PartnerPlacementsColumnDefinition>;

  const departmentName = {
    Header: () => (
      <Header>{t('placements.table_headers.department', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'departmentName',
    width: '150px',
    Cell: DepartmentCell,
  } as Column<PartnerPlacementsColumnDefinition>;

  const jobName = {
    Header: () => (
      <Header>{t('placements.table_headers.job_title', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'jobName',
    width: '150px',
    Cell: JobCell,
  } as Column<PartnerPlacementsColumnDefinition>;

  const jobType = {
    Header: () => (
      <Header>{t('placements.table_headers.job_type', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'jobType',
    width: '150px',
    Cell: JobTypeCell,
  } as Column<PartnerPlacementsColumnDefinition>;

  const scheduledTime = {
    Header: () => (
      <Header>{t('placements.table_headers.date_time', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'dateTime',
    Cell: DateTimeCell,
    width: '200px',
  } as Column<PartnerPlacementsColumnDefinition>;

  const action = {
    Header: () => (
      <Header>{t('placements.table_headers.action', { ns: 'partner-screen-specific' })}</Header>
    ),
    accessor: 'shiftId',
    Cell: ActionCell,
    width: '65px',
  } as Column<PartnerPlacementsColumnDefinition>;

  return [
    workerName,
    companyBranchOfficeInfo,
    departmentName,
    jobName,
    jobType,
    scheduledTime,
    action,
  ];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const WorkerCell = (cell: PartnerPlacementsCellProp<'workerName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const CompanyBranchOfficeInfoCell = (
  cell: PartnerPlacementsCellProp<'companyBranchOfficeInfo'>
) => {
  return (
    <Tooltip
      placement="bottom-start"
      title={
        <Box>
          {cell.value.companyName}
          <br />
          {cell.value.branchOfficeName}
        </Box>
      }
    >
      <BoxInfo>
        <TypographyWithPreLine variant="body1" noWrap>
          {`${cell.value.companyName} \n ${cell.value.branchOfficeName}`}
        </TypographyWithPreLine>
      </BoxInfo>
    </Tooltip>
  );
};

const JobCell = (cell: PartnerPlacementsCellProp<'jobName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const JobTypeCell = (cell: PartnerPlacementsCellProp<'jobType'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const DepartmentCell = (cell: PartnerPlacementsCellProp<'departmentName'>) => {
  return (
    <Tooltip placement="bottom-start" title={cell.value}>
      <Typography variant="body1" noWrap>
        {cell.value}
      </Typography>
    </Tooltip>
  );
};
const DateTimeCell = (cell: PartnerPlacementsCellProp<'dateTime'>) => {
  return (
    <BoxInfo>
      <Typography variant="body1" color="secondary">
        {cell.value.fromDateTime.toFormat('dd MMM yyyy')}
      </Typography>
      <BoxTime>
        <IconAccessTime />
        <Typography variant="body1">
          {cell.value.fromDateTime.toFormat('HH:mm') +
            '-' +
            cell.value.toDateTime.toFormat('HH:mm')}
        </Typography>
      </BoxTime>
    </BoxInfo>
  );
};

const ActionCell = (cell: PartnerPlacementsCellProp<'shiftId'>) => {
  return (
    <BoxAction>
      <BoxIcon>
        <IconIsolationMode
          data-testid={`planner-table-action-${cell.value}`}
          onClick={() => {
            cell.openShiftDetail(cell.value);
          }}
        />
      </BoxIcon>
    </BoxAction>
  );
};
const TypographyWithPreLine = styled(Typography)(({ theme }) => ({
  whiteSpace: 'pre-line',
  color: theme.palette.text.secondary,
}));

const BoxInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
  alignItems: 'center',
}));
const BoxTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  gap: theme.spacing(0.5),
}));

export { getPartnerPlacementsColumnDefinition };
